import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { TutorScreen } from "../../screens/tutor/TutorScreen";
import { ScreenState } from "../../components/ScreenState";
import { TabBar } from "../../components/TabBar";
import { useAsync } from "../../lib/useAsync";
import { tutorStarters, tutorThread, tutorTurn, type TutorAction } from "../../api/tutor";
import { TutorStartersScreen } from "../../screens/tutor/TutorStartersScreen";
import { useUpgradeOnPaywall } from "../../lib/upgrade";
import { fetchMe } from "../../api/me";
import { useActiveExam } from "../../lib/activeExam";
import { ExamSwitcher } from "../../components/ExamSwitcher";
import { updateProfile } from "../../api/profile";
import { ApiError } from "../../api/client";

/** The button label for each action the engine offers back. */
const LABELS: Record<TutorAction, string> = {
  explain: "Explain this",
  explain_differently: "Explain it differently",
  similar_question: "Give me a similar question",
  why_others_wrong: "Why are the other options wrong?",
  ask_specific: "Ask something specific…",
  answer_attempt: "Answer",
};

/**
 * The tutor, on the real Gemma4 engine.
 *
 * The reply used to be a paragraph hardcoded in the router — the same
 * electrolysis explanation whatever question a student asked about. The
 * follow-up buttons come from the engine too: it decides what makes sense next,
 * and an empty list means the exchange limit is reached.
 */
/**
 * The tab's cold entry: no question, so offer the ones worth asking about.
 *
 * Tapping Tutor in the tab bar used to land on a screen that immediately
 * failed with "We couldn't load that." — the route needs a question in its
 * navigation state and the tab bar cannot supply one. The failure also hid the
 * tab bar, because it lives inside the screen the error replaced, so the
 * student was stuck with no way back except the browser.
 */
function TutorStartersRoute() {
  const navigate = useNavigate();
  // Both re-ask on an exam switch: the mistakes and weak topics are that exam's.
  const exam = useActiveExam();
  const starters = useAsync(tutorStarters, [exam]);
  const me = useAsync(fetchMe, [exam]);

  const handleSaveName = async (firstName: string) => {
    await updateProfile({ firstName });
    me.reload();
  };

  return (
    <ScreenState
      loading={starters.loading || me.loading}
      error={starters.error}
      onRetry={starters.reload}
      footer={<TabBar />}
      header={<ExamSwitcher exams={me.data?.exams} active={me.data?.activeExam} />}
    >
      {starters.data ? (
        <TutorStartersScreen
          firstName={me.data?.student.firstName ?? "there"}
          subjects={starters.data.bySubject.map((group) => ({
            subjectId: group.subjectId,
            subject: group.subject,
            questions: group.questions.map((entry) => ({
              questionId: entry.questionId,
              stem: entry.stem,
              source: entry.source,
            })),
          }))}
          topics={starters.data.weakTopics.map((topic) => ({
            topicId: topic.topicId,
            name: topic.name,
            subject: topic.subject,
          }))}
          onPickQuestion={(questionId) => {
            const picked = starters.data?.bySubject
              .flatMap((group) => group.questions)
              .find((entry) => entry.questionId === questionId);
            if (!picked) return;

            void navigate("/tutor", {
              state: {
                questionId: picked.questionId,
                subject: picked.subject,
                source: picked.source,
                stem: picked.stem,
                studentAnswer: picked.studentAnswer,
                correctAnswer: picked.correctAnswer,
              },
            });
          }}
          onPickTopic={(topicId) => {
            const picked = starters.data?.weakTopics.find((topic) => topic.topicId === topicId);
            if (!picked) return;

            void navigate("/practice/session", {
              state: {
                subject: picked.subject,
                subjectId: picked.subjectId,
                source: picked.name,
                topicIds: [picked.topicId],
              },
            });
          }}
          onPractise={() => void navigate("/practice")}
          onSaveName={handleSaveName}
        />
      ) : null}
    </ScreenState>
  );
}

export function TutorRoute() {
  const location = useLocation();
  const hasQuestion = Boolean((location.state as { questionId?: string } | null)?.questionId);

  // Two different screens behind one route: split before any hook runs on a
  // question that is not there.
  return hasQuestion ? <PinnedTutorRoute /> : <TutorStartersRoute />;
}

function PinnedTutorRoute() {
  const navigate = useNavigate();
  const {
    questionId,
    subject = "",
    source = "",
    stem = "",
    studentAnswer,
    correctAnswer,
  } = (useLocation().state ?? {}) as {
    questionId?: string;
    subject?: string;
    source?: string;
    stem?: string;
    studentAnswer?: string | null;
    correctAnswer?: string | null;
  };

  const [reply, setReply] = useState<string | null>(null);
  const [actions, setActions] = useState<TutorAction[]>([]);
  const [asking, setAsking] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  /*
   * Resume before asking.
   *
   * A student who refreshes, or comes back to a question they asked about
   * yesterday, should find the conversation where they left it — the engine
   * holds it against `<studentId>:<questionId>`. Only when there is nothing to
   * resume do we spend a turn on the opening explanation.
   */
  const opening = useAsync(async () => {
    if (!questionId) throw new Error("No question to tutor on.");

    const existing = await tutorThread(questionId);
    const lastTutorTurn = [...existing.turns].reverse().find((turn) => turn.role === "tutor");

    if (lastTutorTurn) {
      return { reply: lastTutorTurn.content, nextActions: [] as TutorAction[] };
    }

    const turn = await tutorTurn({
      questionId,
      action: "explain",
      ...(studentAnswer ? { studentAnswer } : {}),
    });
    return { reply: turn.reply, nextActions: turn.nextActions };
  }, [questionId]);

  const take = async (action: TutorAction, studentMessage?: string) => {
    if (!questionId || asking) return;
    setAsking(true);
    setFailure(null);

    try {
      const turn = await tutorTurn({
        questionId,
        action,
        ...(studentAnswer ? { studentAnswer } : {}),
        ...(studentMessage ? { studentMessage } : {}),
      });
      setReply(turn.reply);
      setActions(turn.nextActions);
    } catch (error) {
      // The tutor failing must never read as practice failing — they are
      // separate, and the copy says so.
      setFailure(
        error instanceof ApiError ? error.message : "The tutor couldn't answer. Try again.",
      );
    } finally {
      setAsking(false);
    }
  };

  // Running out of free questions locks the tutor with everything else (J4).
  useUpgradeOnPaywall(opening.error);

  const current = reply ?? opening.data?.reply ?? "";
  const currentActions = reply !== null ? actions : (opening.data?.nextActions ?? []);

  return (
    <ScreenState
      loading={opening.loading}
      error={opening.error}
      onRetry={opening.reload}
      footer={<TabBar />}
    >
      <TutorScreen
        context={{
          subject,
          source,
          questionNumber: 0,
          summary: studentAnswer
            ? `you answered ${studentAnswer}${correctAnswer ? `, correct was ${correctAnswer}` : ""}`
            : "",
        }}
        question={stem}
        answer={failure ?? (asking ? "Thinking…" : current)}
        suggestions={currentActions.map((action) => LABELS[action])}
        onAsk={(label) => {
          const action = (Object.keys(LABELS) as TutorAction[]).find(
            (key) => LABELS[key] === label,
          );
          // A free-text question comes back as its own text, not a label.
          if (action && action !== "ask_specific") {
            void take(action);
            return;
          }
          void take("ask_specific", label);
        }}
        onBackToPractice={() => void navigate(-1)}
      />
    </ScreenState>
  );
}
